import { Button, Col, Flex, Modal, Row, Table } from "antd";
import PageHeader from "../../components/PageHeader";
import { useEffect, useState } from "react";
import { useAPI } from "../../hooks/useAPI";
import { ENDPOINTS } from "../../constants/endpoints.constant";
import { getCall, postCall } from "../../constants/apicalls.constant";
import { formatDisplayDate } from "../../utils/DateAndTimeConvertor";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import type { ColumnsType } from "antd/es/table";
import { fetchMyBankData } from "../../store/slices/banklist.slice";
import { useNavigate } from "react-router";
import PayoutCurrencySummaryCard from "../../components/payouts/PayoutCurrencySummaryCard";
import {
  normalizePayoutCurrencyTotals,
  PayoutCurrencyTotals,
} from "../../utils/payoutDisplay";
import getCurrencyWithSymbol from "../../utils/getCurrencySymbol";
import PayoutStatusCell from "../../components/payouts/PayoutStatusCell";
import PayoutRequestForm from "./workspace-payout-form";
import { message } from "antd";

type DataType = {
  id: string;
  amount: string;
  currency?: string;
  created_at: string;
  status: string;
  admin_note?: string | null;
  bank_account: {
    id: string;
    display_label: string;
    bank_name: string;
  };
};

type PayoutListResponse = {
  status: boolean;
  message: string;
  data: {
    available_payout_amount: PayoutCurrencyTotals | Record<string, unknown>;
    current_page: number;
    last_page: number;
    total: number;
    per_page: number;
    data: DataType[];
  };
};

const Payouts = () => {
  const [page, setPage] = useState(1);
  const [isWithDrawOpen, setIsWithDrawOpen] = useState(false);

  const event = useSelector((state: RootState) => state.event);
  const user = useSelector((state: RootState) => state.user);
  const organizerCommission = useSelector(
    (state: RootState) => state.user?.organization?.commission,
  );
  const { data } = useSelector((state: RootState) => state.banklist);
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();

  const { loading: payoutLoading, trigger: submitPayout } = useAPI<{
    status: boolean;
    message: string;
  }>(ENDPOINTS.payout);

  const columns: ColumnsType<DataType> = [
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (value: string | number, record: any) => {
        const numeric = typeof value === "string" ? Number(value) : value;
        return getCurrencyWithSymbol(record?.currency || "INR", numeric);
      },
    },
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      render: (value) => formatDisplayDate(value),
    },
    {
      title: "Bank Account",
      key: "bank_account",
      render: (_, record) => (
        <span>{record?.bank_account?.display_label || "N/A"}</span>
      ),
    },
    {
      title: "Status",
      key: "status",
      render: (_, record) => (
        <PayoutStatusCell
          status={record?.status}
          record={record as Record<string, unknown>}
        />
      ),
    },
  ];

  const { response, loading, error, trigger } = useAPI<PayoutListResponse>(
    ENDPOINTS.payout,
    true,
    [],
    getCall({
      limit: 8,
      page: page,
      with: ["bankAccount"],
      event_id: event.id,
      filter: { event_id: event.id },
    }),
  );

  const getListing = async () => {
    await trigger(
      getCall({
        limit: 8,
        page: page,
        with: ["bankAccount"],
        event_id: event.id,
        filter: { event_id: event.id },
      }),
    );
  };

  useEffect(() => {
    dispatch(fetchMyBankData({ id: event.id! }));
  }, []);

  if (error) return <>something went wrong</>;

  const payoutData = response?.data?.available_payout_amount;
  const totals = payoutData
    ? normalizePayoutCurrencyTotals(payoutData as Record<string, unknown>)
    : null;

  const availablePayout = totals?.available_payout ?? 0;

  const handlePayoutSubmit = async (values: {
    amount: string;
    bank_account_id: string;
    organizer_note?: string;
  }) => {
    const res = await submitPayout(
      postCall({
        ...values,
        workspace_id: user?.workspace?.id,
        event_id: event.id,
        currency: "INR",
      }),
    );
    if (res?.status) {
      setIsWithDrawOpen(false);
      getListing();
      message.success("Request sent successfully");
    } else {
      message.error(res?.message || "Failed to submit payout request");
    }
  };

  return (
    <>
      <div className="p-24 w-full max-w-full">
        <PageHeader
          title="Payouts"
          backBtn={false}
          description="View your payout history and stay updated on your earnings and withdrawals."
          additionalButton={
            event?.status === "completed"
              ? [
                  {
                    name: "Withdraw Amount",
                    route: "",
                    fn: () => setIsWithDrawOpen(true),
                  },
                ]
              : []
          }
        />

        {totals && (
          <Row className="mb-[24px] mt-24 md:mt-0 w-full">
            <Col xs={24}>
              <PayoutCurrencySummaryCard
                totals={totals}
                summaryLabel="Event Payout Summary"
                organizerChargePercentage={organizerCommission ?? event?.commission}
                payByParticipant={event?.add_convenience_fees}
              />
            </Col>
          </Row>
        )}

        <Table
          bordered
          rowKey={"id"}
          size="small"
          scroll={{ x: "max-content" }}
          className="mt-30"
          columns={columns}
          loading={loading}
          dataSource={response?.data?.data}
          pagination={{
            current: response?.data?.current_page,
            pageSize: response?.data?.per_page,
            total: response?.data?.total,
            onChange: (page) => setPage(page),
          }}
        />
      </div>

      <Modal
        open={isWithDrawOpen}
        onCancel={() => setIsWithDrawOpen(false)}
        footer={null}
        title="Withdraw Amount"
        destroyOnClose
      >
        {data?.data?.data && data?.data?.data.length > 0 ? (
          <PayoutRequestForm
            maxAmount={availablePayout}
            loading={payoutLoading}
            onSubmit={handlePayoutSubmit}
            onBack={() => setIsWithDrawOpen(false)}
          />
        ) : (
          <>
            <h5 className="text-black text-sm">
              No bank details found. Please add bank details first to withdraw
              money.
            </h5>
            <Flex gap={20} className="mt-10!" justify="end">
              <Button
                type="primary"
                onClick={() => navigate("/workspace?tab=kyc")}
              >
                Add Bank Account
              </Button>
            </Flex>
          </>
        )}
      </Modal>
    </>
  );
};

export default Payouts;
